import { useCallback } from "react";

import { INotification } from "@src/types/api";

import {
    sendCoiNotification as sendCoiNotificationAPI,
    sendLogoNotification as sendLogoNotificationAPI,
    sendRosterNotification as sendRosterNotificationAPI,
} from "@api";

import { useAppSelector } from "@hooks/reduxHooks";

const useClubhouseNotifications = (
    setNotifications: React.Dispatch<
        React.SetStateAction<Array<INotification>>
    >,
    emitToast: (message: string) => void,
) => {
    const { teams } = useAppSelector(state => state.clubhouse);

    const sendLogoNotification = useCallback(
        (teamId: number) => (_event: React.MouseEvent<SVGSVGElement>) => {
            void sendLogoNotificationAPI(teamId);

            const team = teams.find(t => t.id === teamId);

            setNotifications(prev => [
                ...prev.filter(
                    n => n.id !== teamId || n.notificationType !== "logo",
                ),
                {
                    id: teamId,
                    notificationType: "logo",
                    sentAt: new Date().toISOString(),
                },
            ]);
            emitToast(
                `Logo notification sent successfully${team?.name ? ` to ${team.name}` : ""}!`,
            );
        },
        [setNotifications, emitToast],
    );

    const sendRosterNotification = useCallback(
        (teamId: number) => (_event: React.MouseEvent<SVGSVGElement>) => {
            void sendRosterNotificationAPI(teamId);

            const team = teams.find(t => t.id === teamId);

            setNotifications(prev => [
                ...prev.filter(
                    n => n.id !== teamId || n.notificationType !== "roster",
                ),
                {
                    id: teamId,
                    notificationType: "roster",
                    sentAt: new Date().toISOString(),
                },
            ]);
            emitToast(
                `Roster notification sent successfully${team?.name ? ` to ${team.name}` : ""}!`,
            );
        },
        [setNotifications, emitToast],
    );

    const sendCoiNotification = useCallback(
        (teamId: number) => (_event: React.MouseEvent<SVGSVGElement>) => {
            void sendCoiNotificationAPI(teamId);

            const team = teams.find(t => t.id === teamId);

            setNotifications(prev => [
                ...prev.filter(
                    n => n.id !== teamId || n.notificationType !== "coi",
                ),
                {
                    id: teamId,
                    notificationType: "coi",
                    sentAt: new Date().toISOString(),
                },
            ]);
            emitToast(
                `COI notification sent successfully${team?.name ? ` to ${team.name}` : ""}!`,
            );
        },
        [setNotifications, emitToast],
    );

    return {
        sendLogoNotification,
        sendRosterNotification,
        sendCoiNotification,
    };
};

export default useClubhouseNotifications;
